// src/components/apps/chusho-app/ChushoAppBottomNav.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    Box,
    keyframes
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import QuizIcon from '@mui/icons-material/Quiz';
import SettingsIcon from '@mui/icons-material/Settings';
import MapIcon from '@mui/icons-material/Map';
import ForumIcon from '@mui/icons-material/Forum';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackNavigationEvent } from '@/utils/analytics';

export const NAV_HEIGHT = 72;

// ゆらゆらアニメーション
const sway = keyframes`
  0%, 100% {
    transform: translateX(0px);
    opacity: 0.4;
  }
  50% {
    transform: translateX(-3px);
    opacity: 0.7;
  }
`;

const ChushoAppBottomNav: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const appId = 'chusho-app';
    const scrollRef = useRef<HTMLDivElement>(null);

    const getInitialValue = () => {
        if (location.pathname === `/${appId}` || location.pathname === `/${appId}/`) return 0;
        if (location.pathname.startsWith(`/${appId}/subjects`)) return 1;
        if (location.pathname.startsWith(`/${appId}/community`)) return 2;
        if (location.pathname.startsWith(`/${appId}/ai-roadmap`)) return 3;
        if (location.pathname.startsWith(`/${appId}/settings`)) return 4;
        return 0;
    };

    const [value, setValue] = useState(getInitialValue());
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    useEffect(() => {
        if (location.pathname === `/${appId}` || location.pathname === `/${appId}/`) setValue(0);
        else if (location.pathname.startsWith(`/${appId}/subjects`)) setValue(1);
        else if (location.pathname.startsWith(`/${appId}/community`)) setValue(2);
        else if (location.pathname.startsWith(`/${appId}/ai-roadmap`)) setValue(3);
        else if (location.pathname.startsWith(`/${appId}/settings`)) setValue(4);
        else setValue(0);
    }, [location.pathname]);

    // スクロール可能かどうかをチェック
    const checkScrollable = () => {
        const el = scrollRef.current;
        if (!el) return;

        const isScrollable = el.scrollWidth > el.clientWidth;
        const isAtStart = el.scrollLeft <= 1;
        const isAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

        setShowLeftArrow(isScrollable && !isAtStart);
        setShowRightArrow(isScrollable && !isAtEnd);
    };

    // 初期チェックとリサイズ時のチェック
    useEffect(() => {
        // DOMが完全にレンダリングされた後にチェック
        const timer = setTimeout(checkScrollable, 100);
        window.addEventListener('resize', checkScrollable);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', checkScrollable);
        };
    }, []);

    // 値が変更されたときもチェック
    useEffect(() => {
        checkScrollable();
    }, [value]);

    // スクロールイベント
    const handleScroll = () => {
        checkScrollable();
    };

    const handleChange = (_: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        const destinations = ['home', 'subjects', 'community', 'ai-roadmap', 'settings'];
        trackNavigationEvent.bottomNavClick(destinations[newValue] || 'home');
        switch (newValue) {
            case 0:
                navigate(`/${appId}`);
                break;
            case 1:
                navigate(`/${appId}/subjects`);
                break;
            case 2:
                navigate(`/${appId}/community`);
                break;
            case 3:
                navigate(`/${appId}/ai-roadmap`);
                break;
            case 4:
                navigate(`/${appId}/settings`);
                break;
            default:
                navigate(`/${appId}`);
        }
    };

    const commonSx = {
        flex: '1 1 0',
        minWidth: 0,
        maxWidth: 'none',
        color: 'rgba(242,244,248,0.62)',
        position: 'relative',
        '&.Mui-selected': {
            color: '#E4B95B', // ブランドのゴールド
            backgroundColor: 'rgba(228,185,91,0.10)'
        }
    };

    return (
        <Paper
            sx={{
                position: 'fixed',
                inset: `auto 0 0 0`,
                paddingBottom: 'env(safe-area-inset-bottom)',
                zIndex: 1200
            }}
            elevation={3}
            square
        >
            <Box sx={{ position: 'relative' }}>
                {/* 左側の三角インジケーター */}
                {showLeftArrow && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            pointerEvents: 'none',
                            animation: `${sway} 2s ease-in-out infinite`,
                            transformOrigin: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                width: 0,
                                height: 0,
                                borderTop: '8px solid transparent',
                                borderBottom: '8px solid transparent',
                                borderRight: '12px solid rgba(255, 255, 255, 0.6)',
                            }}
                        />
                    </Box>
                )}

                {/* 右側の三角インジケーター */}
                {showRightArrow && (
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%) scaleX(-1)',
                            zIndex: 1,
                            pointerEvents: 'none',
                            animation: `${sway} 2s ease-in-out infinite`,
                            transformOrigin: 'center'
                        }}
                    >
                        <Box
                            sx={{
                                width: 0,
                                height: 0,
                                borderTop: '8px solid transparent',
                                borderBottom: '8px solid transparent',
                                borderRight: '12px solid rgba(255, 255, 255, 0.6)',
                            }}
                        />
                    </Box>
                )}

                <Box
                    ref={scrollRef}
                    onScroll={handleScroll}
                    sx={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        scrollbarWidth: 'none'
                    }}
                >
                    <BottomNavigation
                        value={value}
                        onChange={handleChange}
                        showLabels
                        sx={{
                            backgroundColor: 'rgba(14,17,22,0.92)', // 深インク
                            backdropFilter: 'blur(12px)',
                            borderTop: '1px solid rgba(228,185,91,0.14)',
                            minHeight: NAV_HEIGHT,
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'flex-start',
                            minWidth: 'max-content'
                        }}
                    >
                        <BottomNavigationAction
                            label="ホーム"
                            icon={<HomeIcon />}
                            sx={commonSx}
                        />
                        <BottomNavigationAction
                            label="科目"
                            icon={<QuizIcon />}
                            sx={commonSx}
                        />
                        <BottomNavigationAction
                            label="コミュニティ"
                            icon={<ForumIcon />}
                            sx={commonSx}
                        />
                        <BottomNavigationAction
                            label="AIロードマップ"
                            icon={<MapIcon />}
                            sx={commonSx}
                        />
                        <BottomNavigationAction
                            label="設定"
                            icon={<SettingsIcon />}
                            sx={commonSx}
                        />
                    </BottomNavigation>
                </Box>
            </Box>
        </Paper>
    );
};

export default ChushoAppBottomNav;
